/**
 * useClampOverflow — measures whether a clamped preview overflows, so a cell
 * can hand off to `PopOutList` only when there is more to show.
 *
 * Attach `containerRef` to an element that stays mounted while the popout is
 * open, and `previewRef` to the clamped preview itself.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DEFAULT_COLLAPSED_HEIGHT } from './PopOutList';

export function useClampOverflow<T extends HTMLElement>(deps: React.DependencyList) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [cellWidth, setCellWidth] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(DEFAULT_COLLAPSED_HEIGHT);

  // Guards against the node being detached or reporting zero height: while
  // PopOutList is open it unmounts the `collapsed` child this ref points at
  // (PopOutList.tsx `{!open && (...)}`), so a stale read here must not flip
  // `isTruncated` to false and unmount the popout out from under the hover.
  const checkTruncation = useCallback(() => {
    // Measured off the container, which stays mounted while the popout is
    // open. The expanded card is sized to give the content exactly this much
    // room, so it re-wraps identically and nothing shifts on open.
    const container = containerRef.current;
    const el = previewRef.current;
    if (!el || !el.isConnected || el.clientHeight === 0) return;
    const truncated = el.scrollHeight > el.clientHeight;
    setIsTruncated(truncated);
    // Only while clamped, where clientHeight is the two-line height and does
    // not vary with column width, so this cannot feed the container observer.
    if (!truncated || !container) return;
    setCollapsedHeight(el.clientHeight);
    setCellWidth(container.getBoundingClientRect().width);
  }, []);

  useLayoutEffect(() => {
    checkTruncation();

    // Inter loads with display=swap, so the first measurement is fallback
    // metrics. Only worth re-running before the fonts land.
    if (!document.fonts || document.fonts.status === 'loaded') {
      return undefined;
    }
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) checkTruncation();
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkTruncation, ...deps]);

  // Observes the stable outer container (not the preview that PopOutList
  // unmounts on open) so a `table-fixed` column width change re-measures
  // regardless of hover state.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(container);
    return () => observer.disconnect();
  }, [checkTruncation]);

  return { containerRef, previewRef, isTruncated, cellWidth, collapsedHeight };
}
